import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import VideocamRoundedIcon from '@mui/icons-material/VideocamRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import PsychologyRoundedIcon from '@mui/icons-material/PsychologyRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import Button from '@mui/material/Button';
import { JobPostConfigStepId } from '../interfaces';

interface StepHeaderProps {
  stepId: JobPostConfigStepId;
  stepNumber: number;
  title: string;
  isActive: boolean;
  isCompleted: boolean;
  onSelectStep?: () => void;
}

const getStepIcon = (stepId: JobPostConfigStepId) => {
  switch (stepId) {
    case 'settings':
      return <TuneRoundedIcon sx={{ fontSize: 18 }} />;
    case 'video':
      return <VideocamRoundedIcon sx={{ fontSize: 18 }} />;
    case 'prequal':
      return <HelpOutlineRoundedIcon sx={{ fontSize: 18 }} />;
    case 'cca':
      return <PsychologyRoundedIcon sx={{ fontSize: 18 }} />;
    default:
      return null;
  }
};

export const StepHeader = ({
  stepId,
  stepNumber,
  title,
  isActive,
  isCompleted,
  onSelectStep,
}: StepHeaderProps) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        {/* Step Indicator Avatar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: '50%',
            fontWeight: 700,
            fontSize: '0.8rem',
            flexShrink: 0,
            transition: 'all 0.2s ease',
            bgcolor: isCompleted
              ? 'success.main'
              : isActive
              ? 'primary.main'
              : 'grey.300',
            color: isCompleted || isActive ? '#ffffff' : 'text.secondary',
            boxShadow: isActive
              ? '0 2px 8px rgba(99, 102, 241, 0.3)'
              : isCompleted
              ? '0 2px 8px rgba(16, 185, 129, 0.2)'
              : 'none',
          }}
        >
          {isCompleted && !isActive ? (
            <CheckRoundedIcon sx={{ fontSize: 16 }} />
          ) : (
            stepNumber
          )}
        </Box>

        {/* Step Category Icon */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: isActive
              ? 'primary.main'
              : isCompleted
              ? 'success.main'
              : 'text.secondary',
          }}
        >
          {getStepIcon(stepId)}
        </Box>

        {/* Step Title */}
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            color: isActive
              ? 'primary.dark'
              : isCompleted
              ? 'text.primary'
              : 'text.secondary',
            fontSize: '0.95rem',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </Typography>
      </Box>

      {/* Right Action / Status Badge */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        {isActive ? (
          <Chip
            size="small"
            label="Editing"
            color="primary"
            variant="filled"
            sx={{
              height: 24,
              fontSize: '0.7rem',
              fontWeight: 700,
              borderRadius: '6px',
            }}
          />
        ) : (
          <Button
            size="small"
            variant="outlined"
            onClick={onSelectStep}
            startIcon={<EditRoundedIcon sx={{ fontSize: 14 }} />}
            sx={{
              height: 28,
              fontSize: '0.75rem',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '8px',
              borderColor: 'divider',
              color: 'text.secondary',
              bgcolor: 'background.paper',
              '&:hover': {
                borderColor: 'primary.main',
                color: 'primary.main',
                bgcolor: 'primary.50',
              },
            }}
          >
            Edit
          </Button>
        )}
      </Box>
    </Box>
  );
};
